import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, EventEmitter,
  inject, Input,
  OnDestroy,
  OnInit, Output,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatError } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { TinyMCEModule } from '@sbd-doug-frontend/shared/lib/ui/tinymce/tinymce.module';
import {
  CIPXScrollableContentComponent,
} from '@sbd-doug-frontend/shared/lib/ui';
import { OpenQuoteRequestsItem } from '../dashboard/open-quote-requests-widget/quote-requests.types';
import { BondsListItem } from '../bonds/bonds-list/bonds-list.type';
import { QuoteRequestsListItem } from '../quote/requests-list/quote-request.type';
import { UserService } from '../../core/user/user.service';
import { CathyService } from '../../services/cathy.service';
import { TasksetsService } from '../tasksets/tasksets.service';
import { SuretyTeamWidgetService } from '../dashboard/surety-team-widget/surety-team-widget.service';
import {
  FileUploaderModalComponent,
} from '../dashboard/open-quote-requests-widget/file-uploader-modal/file-uploader-modal.component';
import { FileUploadConfig } from '@sbd-doug-frontend/shared/lib/ui/file-upload/file-upload.component';


@Component({
  selector: 'sbd-account-portal-frontend-bond-cancellation',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckbox,
    MatIcon,
    MatIconButton,
    MatButton,
    TinyMCEModule,
    MatError,
    CIPXScrollableContentComponent,
    CurrencyPipe,
    DatePipe,
  ],
  templateUrl: './bond-cancellation.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./bond-cancellation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BondCancellationComponent implements OnInit, OnDestroy {
  @Input() currentOpenQR: OpenQuoteRequestsItem | BondsListItem | QuoteRequestsListItem;
  @Output() requestSubmitted = new EventEmitter<boolean>();
  @Output() canSubmitChange = new EventEmitter<boolean>();

  _fb = inject(FormBuilder);
  _changeDetector = inject(ChangeDetectorRef);
  _userService = inject(UserService);
  _cathyService = inject(CathyService);
  _taskSetsService = inject(TasksetsService);
  _suretyTeamService = inject(SuretyTeamWidgetService);
  _dialog = inject(MatDialog);

  cancellationForm: FormGroup;
  queuedFiles: File[] = [];
  isSubmitting = false;

  _unsubscribeAll: Subject<void> = new Subject<void>();

  get item() {
    return this.currentOpenQR;
  }

  get bondName() {
    return this.item?.quote_request_bond?.surety_bond_label;
  }

  get bondAmount() {
    return this.item?.selected_quote?.bond_amount;
  }

  get principalName() {
    return this.item?.quote_request_bond?.company_name || `${this.item?.user_first_name ?? ''} ${this.item?.user_last_name ?? ''}`.trim();
  }

  get expirationDate() {
    return this.item?.selected_quote?.bond_expiration_date;
  }

  get canSubmit(): boolean {
    return this.hasExplanationText() && this.cancellationForm?.get('acknowledgment')?.value === true && !this.isSubmitting;
  }

  ngOnInit(): void {
    this.cancellationForm = this._fb.group({
      explanation: ['', [Validators.required]],
      acknowledgment: [false, [Validators.requiredTrue]],
    });

    this.cancellationForm.valueChanges
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this.emitCanSubmitChange());
    this.emitCanSubmitChange();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  openFileUploader() {
    const dialogData = {
      context: 'cipxtask',
      uploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        noIcon: false,
        horizontal: true,
      },
      defaultUploadConfig: <FileUploadConfig>{
        caption: '<p class="text-md">Drag Files Here or Click to Browse</p>',
        noButton: true,
        noIcon: false,
        horizontal: true,
      },
    };

    const dialogRef = this._dialog.open(FileUploaderModalComponent, {
      data: { ...dialogData },
      width: '20vw',
      height: '200pxvw',
      maxWidth: '80vw',
      maxHeight: '70vw',
      minHeight: '200px',
      minWidth: '300px',
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.queuedFiles = [...this.queuedFiles, ...Array.from(result.data.queuedFiles as File[])];
        this._changeDetector.detectChanges();
      }
    });
  }

  deleteFile(index: number) {
    this.queuedFiles.splice(index, 1);
    this._changeDetector.detectChanges();
  }

  submit() {
    if (this.cancellationForm.invalid || this.isSubmitting) {
      if (this.cancellationForm.invalid) {
        this.cancellationForm.get('explanation')?.markAsTouched();
        this.cancellationForm.get('acknowledgment')?.markAsTouched();
        this._changeDetector.detectChanges();
      }
      return;
    }

    this.setSubmitting(true);

    const accountManager = this._suretyTeamService.recipientList()?.find(r => r.type_code === 'accountmanager');
    const currentUser = this._userService.currentAccountUser();
    const bondNumber = this.item?.selected_quote?.carrier_reference_number || 'N/A';

    const subject = `Bond Cancellation Request for ${this.principalName}`;
    const body = `${currentUser.name} requested that bond ${bondNumber} be cancelled. <br><br>Explanation:<br></br>${this.cancellationForm.get('explanation').value}`;

    // Due date: noon next day
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 1);
    dueDate.setHours(12, 0, 0, 0);

    const formData = new FormData();
    formData.append('task_label', subject);
    formData.append('task_description', body);
    formData.append('task_type_code', 'message');
    formData.append('task_due_date', dueDate.toISOString());

    if (accountManager) {
      formData.append('assign_to', JSON.stringify([accountManager]));
    }

    if (this.item?.quote_request_id) {
      formData.append('task_related_entity', JSON.stringify({
        task_related_entity_key: this.item.quote_request_id.toString(),
        task_related_entity_type: 'cipquotereq',
      }));
    }

    this.queuedFiles.forEach((file, index) => {
      formData.append(`task_files[${index}]`, file);
    });

    this._taskSetsService.sendMessage(formData)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (response) => {
          this.setSubmitting(false);
          if (response?.success) {
            this._cathyService.success('Cancellation request submitted successfully.');
            this.requestSubmitted.emit(true);
          } else {
            this._cathyService.error(response?.message || 'Failed to submit cancellation request.');
          }
        },
        error: () => {
          this.setSubmitting(false);
          this._cathyService.error('An error occurred while submitting the request.');
        },
      });
  }

  private hasExplanationText(): boolean {
    const value = this.cancellationForm?.get('explanation')?.value;
    if (typeof value !== 'string' || !value) {
      return false;
    }

    const stripped = value
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&#160;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, '')
      .trim();

    return stripped.length > 0;
  }

  private emitCanSubmitChange(): void {
    this.canSubmitChange.emit(this.canSubmit);
  }

  private setSubmitting(isSubmitting: boolean): void {
    this.isSubmitting = isSubmitting;
    this.emitCanSubmitChange();
    this._changeDetector.detectChanges();
  }
}
